import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Brain,
  Funnel,
  ShieldCheck,
  Target,
  ChartLineUp,
  Compass,
  Sparkle,
  Lightning,
  Warning,
  Users,
  Trophy,
  CurrencyInr,
} from "@phosphor-icons/react";
import Logo from "../components/Logo";
import IndexStrip from "../components/IndexStrip";
import Seo from "../components/Seo";
import { BACKEND_BASE_URL } from "../lib/api";

const publicApi = axios.create({ baseURL: `${BACKEND_BASE_URL}/api` });

function Pillar({ icon: Icon, title, body, testid }) {
  return (
    <div
      className="border border-subtle rounded-sm p-5 bg-surface/60 backdrop-blur-sm hover:border-pos/60 transition-colors"
      data-testid={testid}
    >
      <div className="w-9 h-9 rounded-sm bg-pos-soft border border-pos-soft flex items-center justify-center mb-3">
        <Icon size={16} weight="bold" className="text-pos" />
      </div>
      <div className="font-mono text-xs uppercase tracking-widest text-primary-fg mb-2">{title}</div>
      <div className="text-sm text-secondary-fg leading-relaxed">{body}</div>
    </div>
  );
}

export default function About() {
  const [stats, setStats] = useState({ traders: 500, wins: 0, stocks_scanned: 2400 });

  useEffect(() => {
    publicApi
      .get("/public/stats")
      .then(({ data }) => setStats(data))
      .catch((err) => {
        console.error("/public/stats", err);
      });
  }, []);

  return (
    <div className="min-h-screen bg-app grain" data-testid="about-page">
      <Seo
        title="About · Breakout Scanner — AI-filtered NSE breakouts with clarity"
        description="Our mission: cut out false breakouts, manipulated volume and illiquid stocks. Deliver only decision-ready NSE opportunities with entry, SL, target and breakout quality score."
        url="/about"
      />
      <IndexStrip />

      <div className="py-10 px-6">
        <div className="max-w-5xl mx-auto">
          <Link
            to="/dashboard"
            data-testid="about-back"
            className="mb-8 flex items-center gap-2 text-muted-fg hover:text-primary-fg text-sm font-mono uppercase tracking-widest"
          >
            <ArrowLeft size={14} /> Back
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <Logo size={28} />
            <div className="font-mono text-xs tracking-[0.22em] uppercase text-muted-fg">
              Breakout Scanner
            </div>
          </div>

          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-14"
          >
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-pos mb-3">
              /// About Us
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-primary-fg leading-[1.05] mb-5">
              We are redefining how{" "}
              <span className="text-pos">breakout trading</span> is done.
            </h1>
            <p className="text-lg text-secondary-fg leading-relaxed max-w-3xl">
              In a market full of noise, most traders struggle with false breakouts, manipulated volume
              spikes, and illiquid stocks. Our mission is to{" "}
              <span className="text-primary-fg font-medium">eliminate that noise</span> and deliver only
              high-quality, decision-ready opportunities.
            </p>
          </motion.div>

          {/* What we do */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-14">
            <Pillar
              icon={Brain}
              title="AI-driven filtering"
              body="Every candidate is scored by multiple models — removing fake volume spikes, pump-and-dump patterns and low-conviction setups before they reach you."
              testid="pillar-ai"
            />
            <Pillar
              icon={Funnel}
              title="Technical precision"
              body="6-month and 3-year highs, confirmed by genuine volume expansion and an enforceable EMA-10 exit rule. No guesswork — every signal is deterministic."
              testid="pillar-technical"
            />
            <Pillar
              icon={ShieldCheck}
              title="Fundamental validation"
              body="Gemini-powered fundamental check layered on top of the technical breakout — so you know you're trading a real business, not a thinly-traded shell."
              testid="pillar-fundamental"
            />
          </div>

          {/* Clarity block */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="border border-subtle rounded-sm p-8 md:p-10 bg-surface/60 backdrop-blur-sm mb-14"
          >
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-pos mb-3">
              /// Our promise
            </div>
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-primary-fg mb-4">
              We don't just show data. We provide{" "}
              <span className="text-pos">clarity</span>.
            </h2>
            <p className="text-base text-secondary-fg leading-relaxed max-w-3xl">
              With daily fresh breakouts, F&amp;O insights, sector intelligence and a 1–10 breakout
              quality score, you get a complete system that not only finds opportunities — it tells you{" "}
              <span className="text-primary-fg font-medium">when to enter, where to exit, and how to
              manage risk.</span>
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              {[
                { icon: Target, label: "Entry · SL · Target", testid: "feat-entry" },
                { icon: ChartLineUp, label: "Live F&O OI", testid: "feat-fno" },
                { icon: Compass, label: "Sector Intelligence", testid: "feat-sectors" },
                { icon: Sparkle, label: "1–10 Quality Score", testid: "feat-score" },
              ].map((f) => (
                <div
                  key={f.testid}
                  data-testid={f.testid}
                  className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-secondary-fg"
                >
                  <f.icon size={14} weight="bold" className="text-pos shrink-0" />
                  {f.label}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Mission */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="relative overflow-hidden border border-pos/40 rounded-sm p-8 md:p-12 bg-gradient-to-br from-pos-soft/40 via-surface/60 to-surface/60 mb-14"
          >
            <div
              aria-hidden
              className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-pos/10 blur-3xl"
            />
            <Lightning size={22} weight="bold" className="text-pos mb-3" />
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-pos mb-2">
              /// Our goal
            </div>
            <h2 className="text-2xl md:text-4xl font-medium tracking-tight text-primary-fg leading-snug max-w-3xl">
              To help traders move from{" "}
              <span className="line-through text-muted-fg">confusion</span> to{" "}
              <span className="text-pos">confidence</span> — and from random trades to structured,
              high-probability decisions.
            </h2>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/pricing"
                data-testid="about-cta-pricing"
                className="px-5 py-2.5 rounded-sm text-sm font-medium transition"
                style={{ background: "hsl(var(--pos))", color: "hsl(var(--bg))" }}
              >
                See Pricing →
              </Link>
              <Link
                to="/wins"
                data-testid="about-cta-wins"
                className="px-5 py-2.5 border border-subtle rounded-sm text-primary-fg hover:bg-surface-hover hover:border-pos transition text-sm font-medium"
              >
                Track Record ↗
              </Link>
            </div>
          </motion.div>

          {/* Social Proof + CTA */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="border border-subtle rounded-sm bg-surface/70 backdrop-blur-sm p-8 md:p-10 mb-10"
            data-testid="about-social-proof"
          >
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-pos mb-3">
              /// Community
            </div>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              <div>
                <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-primary-fg leading-tight">
                  Join <span className="text-pos tabular-nums" data-testid="stat-traders">
                    {stats.traders.toLocaleString("en-IN")}+
                  </span>{" "}
                  traders
                </h2>
                <p className="text-sm md:text-base text-secondary-fg mt-2 max-w-lg">
                  Already using Breakout Scanner daily for structured, high-probability NSE trades.
                  Start for ₹5 — cancel anytime.
                </p>
                <div className="mt-5 flex flex-wrap gap-5 text-xs font-mono uppercase tracking-widest">
                  <div className="flex items-center gap-1.5 text-secondary-fg" data-testid="stat-stocks">
                    <ChartLineUp size={13} weight="bold" className="text-pos" />
                    <span className="text-primary-fg tabular-nums">
                      {stats.stocks_scanned.toLocaleString("en-IN")}
                    </span>{" "}
                    NSE stocks scanned
                  </div>
                  <div className="flex items-center gap-1.5 text-secondary-fg" data-testid="stat-wins">
                    <Trophy size={13} weight="bold" className="text-pos" />
                    <span className="text-primary-fg tabular-nums">{stats.wins}</span> target-hit wins
                  </div>
                  <div className="flex items-center gap-1.5 text-secondary-fg">
                    <Users size={13} weight="bold" className="text-pos" />
                    Growing every week
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-3 lg:items-end shrink-0">
                <Link
                  to="/register"
                  data-testid="about-cta-trial"
                  className="group inline-flex items-center gap-2 px-6 py-3 rounded-sm text-sm font-semibold transition shadow-lg shadow-pos/20 hover:shadow-pos/40"
                  style={{ background: "hsl(var(--pos))", color: "hsl(var(--bg))" }}
                >
                  <CurrencyInr size={16} weight="bold" />
                  Start ₹5 Trial
                  <span className="transition-transform group-hover:translate-x-0.5">→</span>
                </Link>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg lg:text-right">
                  5-day trial · ₹299/mo · cancel anytime
                </div>
              </div>
            </div>
          </motion.div>

          {/* Founder / Contact */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="border border-subtle rounded-sm p-6 bg-surface/60 backdrop-blur-sm mb-6"
            data-testid="about-contact"
          >
            <div className="font-mono text-xs uppercase tracking-[0.22em] text-pos mb-4">
              // Founder · Contact
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] gap-5 items-start">
              <div
                className="w-16 h-16 rounded-sm border border-pos/40 bg-pos-soft flex items-center justify-center font-mono text-xl font-bold text-pos shrink-0"
                aria-hidden="true"
              >
                AKC
              </div>
              <div className="min-w-0">
                <div className="text-lg font-semibold text-primary-fg">
                  Dr. Adarsh Kumar Chiranjivi
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-fg mt-0.5">
                  Indian Institute of Technology Guwahati
                </div>
                <div className="text-sm text-secondary-fg mt-2 leading-relaxed">
                  Researcher · Stock Market Analyst · Founder of Breakout Scanner
                </div>
                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                  <a
                    href="mailto:breakoutscanner1987@gmail.com"
                    className="font-mono text-secondary-fg hover:text-pos transition flex items-center gap-1.5"
                    data-testid="about-contact-email"
                  >
                    <span className="text-[10px] uppercase tracking-widest text-muted-fg">Email</span>
                    <span>breakoutscanner1987@gmail.com</span>
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Disclaimer */}
          <div
            className="border border-subtle rounded-sm p-5 bg-surface/40 flex gap-3 items-start mb-10"
            data-testid="about-disclaimer"
          >
            <Warning size={16} weight="bold" className="text-mod shrink-0 mt-0.5" />
            <div className="text-xs text-secondary-fg leading-relaxed">
              <span className="font-mono uppercase tracking-widest text-mod">Disclaimer · </span>
              Share market investments are subject to risk. Please do your own research before
              investing. Breakout Scanner is an educational research tool and does not execute trades
              on your behalf.
            </div>
          </div>

          {/* Footer */}
          <div className="pt-6 border-t border-subtle flex flex-wrap gap-4 text-[10px] font-mono uppercase tracking-widest text-muted-fg">
            <Link to="/terms" className="hover:text-primary-fg">Terms</Link>
            <Link to="/refund" className="hover:text-primary-fg">Refund Policy</Link>
            <Link to="/privacy" className="hover:text-primary-fg">Privacy</Link>
            <Link to="/pricing" className="hover:text-primary-fg">Pricing</Link>
            <Link to="/wins" className="hover:text-primary-fg">Track Record</Link>
            <span className="ml-auto flex flex-wrap gap-x-4">
              <a href="mailto:breakoutscanner1987@gmail.com" className="hover:text-primary-fg">breakoutscanner1987@gmail.com</a>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
